import { JsonRecord } from './deriveColumns'

// The spec marks only some credentials writeOnly, so the key name decides.
// `key` and `apiKey` match whole; the suffixes are anchored at the end so
// `tokenizationKeyId` and `privateKeyExpires` stay visible.
const SECRET_KEY =
  /^(key|apiKey)$|(password|secret|secretKey|token|privateKey)$/i

const MAX_MASK_LENGTH = 16
const MASK = /^\*+$/

export const isSecretKey = (key: string): boolean => SECRET_KEY.test(key)

export const maskSecret = (value: string): string =>
  '*'.repeat(Math.min(value.length, MAX_MASK_LENGTH))

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const mapSecrets = (
  value: unknown,
  onSecret: (secret: string) => string | undefined,
): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => mapSecrets(item, onSecret))
  }
  if (!isRecord(value)) return value

  const result: JsonRecord = {}
  for (const [key, child] of Object.entries(value)) {
    if (isSecretKey(key) && typeof child === 'string' && child !== '') {
      const replaced = onSecret(child)
      if (replaced !== undefined) result[key] = replaced
    } else {
      result[key] = mapSecrets(child, onSecret)
    }
  }
  return result
}

export const redactSecrets = <T extends JsonRecord>(record: T): T =>
  mapSecrets(record, maskSecret) as T

/**
 * An edit body is rebuilt from the redacted record, so an untouched secret
 * comes back as its mask. Drop it, so the save keeps the stored value.
 */
export const dropMaskedSecrets = <T extends JsonRecord>(body: T): T =>
  mapSecrets(body, (secret) => (MASK.test(secret) ? undefined : secret)) as T
